import { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, MapPin, Phone, Package, Clock, CheckCircle } from 'lucide-react';
import ExpressDetailModal from './ExpressDetailModal';

const API_URL = process.env.REACT_APP_API_URL;

function timeAgo(isoString) {
  if (!isoString) return 'Sin datos';
  const diff = Math.floor((Date.now() - new Date(isoString)) / 1000);
  if (diff < 60)   return 'Hace menos de 1 min';
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  return `Hace ${Math.floor(diff / 3600)}h`;
}

export default function ExpressDriversTab({ user_id, deliveries, drivers, setDrivers, updateDelivery }) {
  const [loading, setLoading]       = useState(false);
  const [selectedDelivery, setSelected] = useState(null);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/express/drivers`);
      setDrivers(data);
    } catch (err) {
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (drivers.length === 0) fetchDrivers();
  }, []);

  // Deliveries assigned to each driver (active only)
  function getDriverDeliveries(driverUserId) {
    return deliveries.filter(
      d => d.driver?.user_id === driverUserId && ['pending', 'encamino'].includes(d.status)
    );
  }

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {drivers.length} conductor{drivers.length !== 1 ? 'es' : ''}
          {' · '}
          <span className="text-brand-green font-medium">
            {drivers.filter(d => d.active_express_count === 0).length} disponible{drivers.filter(d => d.active_express_count === 0).length !== 1 ? 's' : ''}
          </span>
        </div>
        <button
          onClick={fetchDrivers}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-brand-primary transition-colors"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {/* Driver grid */}
      {loading && drivers.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">Cargando conductores...</div>
      ) : drivers.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">No hay conductores registrados</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {drivers.map(drv => {
            const activeDeliveries = getDriverDeliveries(drv.user_id);
            const isOnline = drv.last_location_update &&
              (Date.now() - new Date(drv.last_location_update)) < 10 * 60 * 1000; // 10 min
            const isAvailable = drv.active_express_count === 0;

            return (
              <div
                key={drv.user_id}
                className="bg-white rounded-xl border border-gray-200 p-4 space-y-3"
              >
                {/* Driver header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {drv.profile_image ? (
                      <img src={drv.profile_image} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary font-bold text-sm">
                        {drv.username?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-gray-800 text-sm">{drv.username}</div>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Phone size={10} />
                        {drv.phone_number || '—'}
                      </div>
                    </div>
                  </div>

                  {/* Status badge */}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    isAvailable
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {isAvailable ? 'Libre' : `${drv.active_express_count} activa${drv.active_express_count > 1 ? 's' : ''}`}
                  </span>
                </div>

                {/* Location & online status */}
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-400' : 'bg-gray-300'}`} />
                  <span>{isOnline ? 'En línea' : 'Desconectado'}</span>
                  {drv.last_location_update && (
                    <>
                      <span>·</span>
                      <Clock size={10} />
                      <span>{timeAgo(drv.last_location_update)}</span>
                    </>
                  )}
                </div>

                {drv.latitude && drv.longitude && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <MapPin size={11} />
                    <span className="font-mono">{drv.latitude.toFixed(4)}, {drv.longitude.toFixed(4)}</span>
                  </div>
                )}

                {/* Active deliveries */}
                {activeDeliveries.length > 0 && (
                  <div className="border-t border-gray-100 pt-3 space-y-2">
                    <div className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                      <Package size={12} />
                      Entregas activas
                    </div>
                    {activeDeliveries.map(d => (
                      <button
                        key={d.id}
                        onClick={() => setSelected(d.id)}
                        className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-lg px-3 py-2 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-gray-500">#{d.id}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            d.status === 'encamino' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {d.status === 'encamino' ? 'En camino' : 'Pendiente'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 truncate mt-0.5">{d.recipient_name}</div>
                        <div className="text-xs text-gray-400 truncate">{d.dropoff_address}</div>
                      </button>
                    ))}
                  </div>
                )}

                {/* No deliveries */}
                {activeDeliveries.length === 0 && isAvailable && (
                  <div className="flex items-center gap-1.5 text-xs text-green-600 pt-1">
                    <CheckCircle size={12} />
                    Disponible para nuevas entregas
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedDelivery && (
        <ExpressDetailModal
          deliveryId={selectedDelivery}
          drivers={drivers}
          user_id={user_id}
          onClose={() => setSelected(null)}
          onUpdate={updateDelivery}
        />
      )}
    </div>
  );
}